import { Link } from "react-router";
import type { ProductSummary } from "@jipraks/shared";
import { SpotlightCard } from "@client/components/ui/SpotlightCard";
import { authorsCopy } from "@client/locales/id/authors";
import { PriceTag } from "./PriceTag";
import { ProductCardMeta } from "./ProductCardMeta";
import { RecommendedBadge } from "./RecommendedBadge";

interface ProductCardProps {
  product: ProductSummary;
  /** Off where every card has the same author (the author's own page). */
  showAuthor?: boolean;
}

export function ProductCard({ product, showAuthor = true }: ProductCardProps) {
  return (
    <SpotlightCard className="h-full">
      <Link to={`/course/${product.slug}`} className="flex h-full flex-col">
        <div className="relative aspect-video overflow-hidden rounded-t-2xl border-b border-border-default bg-background-elevated">
          {product.imageUrl && (
            <img
              src={product.imageUrl}
              alt={product.title}
              width={640}
              height={360}
              loading="lazy"
              className="size-full object-cover transition-transform duration-300 ease-expo-out group-hover:scale-[1.02]"
            />
          )}
          {product.isHighlighted && <RecommendedBadge />}
        </div>
        <div className="flex flex-1 flex-col gap-3 p-5">
          <ProductCardMeta product={product} />
          <h3 className="line-clamp-2 text-lg leading-snug font-semibold tracking-tight text-foreground">
            {product.title}
          </h3>
          {showAuthor && product.author && (
            // Plain text: the whole card is already a link, and links cannot nest.
            <p className="truncate text-sm text-foreground-muted">
              {authorsCopy.by} <span className="text-foreground">{product.author.name}</span>
            </p>
          )}
          {product.shortDescription && (
            <p className="line-clamp-3 text-sm leading-relaxed text-foreground-muted">
              {product.shortDescription}
            </p>
          )}
          <PriceTag price={product.price} normalPrice={product.normalPrice} className="mt-auto pt-2" />
        </div>
      </Link>
    </SpotlightCard>
  );
}
